import { Heart } from "lucide-react";
import { Link } from "wouter";
import logoUrl from "/logo.png";
import { useMemo } from "react";
import { 
  SiInstagram, 
  SiFacebook, 
  SiWhatsapp, 
  SiLinkedin, 
  SiX, 
  SiSnapchat, 
  SiTiktok 
} from "react-icons/si";

// Calculate Penelope's age based on her birthday (October 10, 2017)
// Using Central US time zone
function calculatePenelopesAge(): number {
  // Get current date in Central US time
  const now = new Date();
  const centralTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  
  // Penelope's birthday: October 10, 2017
  const birthYear = 2017;
  const birthMonth = 9; // October (0-indexed)
  const birthDay = 10;
  
  const currentYear = centralTime.getFullYear();
  const currentMonth = centralTime.getMonth();
  const currentDay = centralTime.getDate();
  
  // Calculate age
  let age = currentYear - birthYear;
  
  // Check if birthday hasn't occurred yet this year
  if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
    age--;
  }
  
  return age;
}

export default function Footer() {
  const penelopesAge = useMemo(() => calculatePenelopesAge(), []);
  
  return (
    <footer className="bg-gradient-to-b from-white to-purple-50 border-t border-purple-100 py-12 mt-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Main footer content */}
          <div className="text-center space-y-6">
            {/* Brand section */}
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2">
                <img
                  src={logoUrl}
                  alt="SnuggleCrafters Logo"
                  className="w-10 h-10 object-contain"
                />
                <h3 className="text-2xl font-bold text-snuggle-gradient">
                  SnuggleCrafters.com
                </h3>
              </div>
              <p className="text-sm text-gray-600 max-w-md mx-auto">
                Where bedtime becomes an adventure. Creating personalized,
                magical stories that spark imagination and sweet dreams.
              </p>
            </div>

            {/* Special thanks section */}
            <div className="bg-purple-50 rounded-2xl px-6 py-4 max-w-lg mx-auto border border-purple-100">
              <p className="text-sm text-purple-700 flex items-center justify-center gap-2">
                <img
                  src={logoUrl}
                  alt="SnuggleCrafters Logo"
                  className="w-4 h-4 object-contain"
                />
                <span className="font-medium">
                  With heartfelt thanks to Penelope, age {penelopesAge}, whose brilliant
                  ideas and endless imagination helped shape every magical
                  feature of this app
                </span>
                <img
                  src={logoUrl}
                  alt="SnuggleCrafters Logo"
                  className="w-4 h-4 object-contain"
                />
              </p>
            </div>

            {/* Social Media Links */}
            <div className="pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Follow Us</p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <a
                  href="https://www.instagram.com/snugglecrafters"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-pink-500 transition-colors"
                  aria-label="Instagram @SnuggleCrafters"
                  data-testid="link-instagram"
                >
                  <SiInstagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.facebook.com/share/1Br2Ja6xoi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-blue-600 transition-colors"
                  aria-label="Facebook @SnuggleCrafters"
                  data-testid="link-facebook"
                >
                  <SiFacebook className="w-5 h-5" />
                </a>
                <a
                  href="https://whatsapp.com/channel/0029VbAq2Qs6BIEoQCjnlE0b"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-green-500 transition-colors"
                  aria-label="WhatsApp Channel"
                  data-testid="link-whatsapp"
                >
                  <SiWhatsapp className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/snugglecrafters"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-blue-700 transition-colors"
                  aria-label="LinkedIn @SnuggleCrafters"
                  data-testid="link-linkedin"
                >
                  <SiLinkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://x.com/snugglecrafters"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-black transition-colors"
                  aria-label="X (Twitter) @SnuggleCrafters"
                  data-testid="link-x"
                >
                  <SiX className="w-5 h-5" />
                </a>
                <a
                  href="https://www.snapchat.com/add/snugglecrafters"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-yellow-400 transition-colors"
                  aria-label="Snapchat @SnuggleCrafters"
                  data-testid="link-snapchat"
                >
                  <SiSnapchat className="w-5 h-5" />
                </a>
                <a
                  href="https://www.tiktok.com/@snugglecrafters"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-black transition-colors"
                  aria-label="TikTok @SnuggleCrafters"
                  data-testid="link-tiktok"
                >
                  <SiTiktok className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Powered by section */}
            <div className="pt-4 space-y-3">
              {/* Legal links */}
              <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
                <Link
                  href="/terms"
                  className="hover:text-purple-600 transition-colors font-medium"
                >
                  Terms of Service
                </Link>
                <span className="text-gray-300">|</span>
                <Link
                  href="/privacy"
                  className="hover:text-purple-600 transition-colors font-medium"
                >
                  Privacy Policy
                </Link>
                <span className="text-gray-300">|</span>
                <Link
                  href="/about"
                  className="hover:text-purple-600 transition-colors font-medium"
                >
                  About Us
                </Link>
                <span className="text-gray-300">|</span>
                <Link
                  href="/buy-gift-card"
                  className="hover:text-purple-600 transition-colors font-medium"
                  data-testid="link-gift-cards-footer"
                >
                  Gift Cards
                </Link>
              </div>

              {/* Copyright */}
              <p className="text-xs text-gray-500 mt-4">
                © 2025{" "}
                <span className="text-snuggle-gradient">SnuggleCrafters.com</span>{" "}
                powered by{" "}
                <a href="https://vibecaas.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-purple-600 underline">VibeCaaS.com</a>{" "}
                a division of{" "}
                <a href="https://neuralquantum.ai" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-purple-600 underline">NeuralQuantum.ai</a>{" "}
                LLC. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
